import { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
    ArrowLeft, Send, Paperclip, X, CheckCircle2, Clock,
    MessageSquare, ChevronRight, AlertCircle, Loader2, Image as ImageIcon, Reply,
} from "lucide-react";
import Sidebar from "../components/Sidebar";
import MobileNav from "../components/MobileNav";
import { useAuth } from "../hooks/useAuth";
import { API_BASE_URL } from "@/utils/env";
import { getAuthTokens } from "@/utils/auth";

type Attachment = { name: string; url: string; type: string; size: number };

type SentMessage = {
    id: string;
    subject: string;
    message: string;
    attachments: Attachment[];
    replyToTitle?: string | null;
    sentAt: string;
};

function timeAgo(iso: string): string {
    try {
        const ms = Date.now() - new Date(iso).getTime();
        const mins = Math.floor(ms / 60000);
        if (mins < 1) return "Just now";
        if (mins < 60) return `${mins}m ago`;
        const hrs = Math.floor(mins / 60);
        if (hrs < 24) return `${hrs}h ago`;
        const days = Math.floor(hrs / 24);
        if (days < 7) return `${days}d ago`;
        return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric" });
    } catch {
        return "";
    }
}

function stripHtml(html: string): string {
    return html.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
}

export default function MessageAdmin() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { user: authUser, logout } = useAuth();
    const fileInputRef = useRef<HTMLInputElement>(null);

    // Pre-fill from notification reply context
    const replyToId = searchParams.get("replyTo");
    const replyToTitle = searchParams.get("replyTitle");

    const [subject, setSubject] = useState(replyToTitle ? `Re: ${replyToTitle}` : "");
    const [message, setMessage] = useState("");
    const [attachments, setAttachments] = useState<Attachment[]>([]);
    const [uploading, setUploading] = useState(false);
    const [sending, setSending] = useState(false);
    const [sent, setSent] = useState(false);
    const [error, setError] = useState("");
    const [sentMessages, setSentMessages] = useState<SentMessage[]>([]);
    const [loadingHistory, setLoadingHistory] = useState(true);
    const [selectedMsg, setSelectedMsg] = useState<SentMessage | null>(null);

    // Fetch sent message history
    useEffect(() => {
        if (!authUser?.uid) return;
        const fetch_ = async () => {
            try {
                const tokens = getAuthTokens();
                const idToken = tokens?.idToken;
                const resp = await fetch(`${API_BASE_URL}/auth/user/${authUser.uid}`, {
                    headers: idToken ? { Authorization: `Bearer ${idToken}` } : {},
                });
                if (resp.ok) {
                    const json = await resp.json();
                    const userData = json.success ? json.user : json;
                    const raw = userData?.messages;
                    if (raw && typeof raw === "object") {
                        const list: SentMessage[] = Object.entries(raw).map(([id, v]: [string, any]) => ({
                            id,
                            subject: v.subject || "(no subject)",
                            message: v.message || "",
                            attachments: v.attachments || [],
                            replyToTitle: v.replyToTitle || null,
                            sentAt: v.sentAt || "",
                        }));
                        list.sort((a, b) => new Date(b.sentAt).getTime() - new Date(a.sentAt).getTime());
                        setSentMessages(list);
                    }
                }
            } catch (e) {
                console.error("Error loading message history:", e);
            } finally {
                setLoadingHistory(false);
            }
        };
        fetch_();
    }, [authUser, sent]);

    const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = Array.from(e.target.files || []);
        if (!files.length) return;
        setUploading(true);
        setError("");
        const newAtts: Attachment[] = [];
        for (const file of files) {
            if (file.size > 10 * 1024 * 1024) {
                setError(`${file.name} exceeds 10 MB limit.`);
                continue;
            }
            // Convert to base64 — no server upload needed, stored inline
            const b64 = await new Promise<string>((resolve, reject) => {
                const reader = new FileReader();
                reader.onload = () => resolve(reader.result as string);
                reader.onerror = reject;
                reader.readAsDataURL(file);
            });
            newAtts.push({ name: file.name, url: b64, type: file.type, size: file.size });
        }
        setAttachments(prev => [...prev, ...newAtts]);
        setUploading(false);
        if (fileInputRef.current) fileInputRef.current.value = "";
    };

    const removeAttachment = (i: number) => setAttachments(prev => prev.filter((_, idx) => idx !== i));

    const handleSend = async () => {
        if (!subject.trim() || !message.trim()) {
            setError("Subject and message are required.");
            return;
        }
        if (!authUser?.uid) return;
        setSending(true);
        setError("");
        try {
            const tokens = getAuthTokens();
            const idToken = tokens?.idToken;
            const resp = await fetch(`${API_BASE_URL}/auth/user-message`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    ...(idToken ? { Authorization: `Bearer ${idToken}` } : {}),
                },
                body: JSON.stringify({
                    subject: subject.trim(),
                    message: message.trim(),
                    attachments,
                    replyToId: replyToId || null,
                    replyToTitle: replyToTitle || null,
                    senderName: `${authUser.displayName || ""}`.trim() || authUser.email || "",
                    senderEmail: authUser.email || "",
                }),
            });
            const json = await resp.json();
            if (!resp.ok || !json.success) throw new Error(json.error || "Failed to send");
            setSent(true);
            setSubject("");
            setMessage("");
            setAttachments([]);
            setTimeout(() => setSent(false), 4000);
        } catch (e: any) {
            setError(e.message || "Failed to send message. Please try again.");
        } finally {
            setSending(false);
        }
    };

    return (
        <div className="flex min-h-screen bg-[#F3F4F6]">
            <Sidebar />
            <main className="flex-1 md:ms-64 overflow-y-auto">
                <MobileNav onLogout={logout} />

                <div className="mx-auto max-w-[1200px] px-4 md:px-6 lg:px-8 py-6 md:py-8">

                    {/* Back */}
                    <button
                        onClick={() => navigate(replyToId ? `/notifications/${replyToId}` : "/notifications")}
                        className="flex items-center gap-2 text-sm font-semibold text-gray-500 hover:text-gray-800 transition mb-6"
                    >
                        <ArrowLeft className="w-4 h-4" />
                        {replyToId ? "Back to Notification" : "Back to Notifications"}
                    </button>

                    {/* Page header */}
                    <div className="mb-6">
                        <h1 className="text-2xl font-black text-gray-900 flex items-center gap-2.5">
                            <div className="w-9 h-9 rounded-xl bg-blue-600 flex items-center justify-center shrink-0">
                                <MessageSquare className="w-5 h-5 text-white" />
                            </div>
                            Message Admin
                        </h1>
                        <p className="text-sm text-gray-500 mt-1 ms-11">Send a message directly to the Conncct team.</p>
                    </div>

                    <div className="flex flex-col lg:flex-row gap-5 items-start">

                        {/* ── Compose panel ── */}
                        <div className="flex-1 min-w-0">
                            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">

                                {/* Reply context banner */}
                                {replyToTitle && (
                                    <div className="flex items-center gap-2 px-6 py-3 bg-blue-50 border-b border-blue-100">
                                        <ChevronRight className="w-4 h-4 text-blue-500 shrink-0" />
                                        <p className="text-xs font-semibold text-blue-700 truncate">
                                            Replying to: <span className="font-black">{replyToTitle}</span>
                                        </p>
                                    </div>
                                )}

                                <div className="p-6 space-y-5">

                                    {/* Subject */}
                                    <div>
                                        <label className="block text-xs font-bold text-gray-500 uppercase tracking-widest mb-2">
                                            Subject
                                        </label>
                                        <input
                                            type="text"
                                            value={subject}
                                            onChange={e => setSubject(e.target.value)}
                                            placeholder="What's this about?"
                                            className="w-full rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm font-semibold text-gray-900 placeholder-gray-400 focus:outline-none focus:border-blue-400 focus:bg-white transition"
                                        />
                                    </div>

                                    {/* Message */}
                                    <div>
                                        <label className="block text-xs font-bold text-gray-500 uppercase tracking-widest mb-2">
                                            Message
                                        </label>
                                        <textarea
                                            value={message}
                                            onChange={e => setMessage(e.target.value)}
                                            placeholder="Write your message here..."
                                            rows={7}
                                            className="w-full rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:border-blue-400 focus:bg-white transition resize-none leading-relaxed"
                                        />
                                        <p className="text-[11px] text-gray-400 mt-1 text-right">{message.length} characters</p>
                                    </div>

                                    {/* Attachments preview */}
                                    {attachments.length > 0 && (
                                        <div className="space-y-2">
                                            {attachments.map((att, i) => {
                                                const isImg = att.type.startsWith("image/");
                                                return (
                                                    <div key={i} className="flex items-center gap-3 bg-gray-50 rounded-xl border border-gray-200 px-3.5 py-2.5">
                                                        <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center shrink-0">
                                                            {isImg
                                                                ? <ImageIcon className="w-4 h-4 text-blue-600" />
                                                                : <Paperclip className="w-4 h-4 text-blue-600" />
                                                            }
                                                        </div>
                                                        <div className="flex-1 min-w-0">
                                                            <p className="text-xs font-semibold text-gray-900 truncate">{att.name}</p>
                                                            <p className="text-[11px] text-gray-400">
                                                                {att.size < 1024 * 1024
                                                                    ? `${(att.size / 1024).toFixed(1)} KB`
                                                                    : `${(att.size / 1024 / 1024).toFixed(1)} MB`}
                                                            </p>
                                                        </div>
                                                        <button
                                                            onClick={() => removeAttachment(i)}
                                                            className="text-gray-400 hover:text-red-500 transition shrink-0"
                                                        >
                                                            <X className="w-4 h-4" />
                                                        </button>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}

                                    {/* Error */}
                                    {error && (
                                        <div className="flex items-center gap-2 rounded-xl bg-red-50 border border-red-200 px-4 py-3">
                                            <AlertCircle className="w-4 h-4 text-red-500 shrink-0" />
                                            <p className="text-sm text-red-700 font-medium">{error}</p>
                                        </div>
                                    )}

                                    {/* Success */}
                                    {sent && (
                                        <div className="flex items-center gap-2 rounded-xl bg-green-50 border border-green-200 px-4 py-3">
                                            <CheckCircle2 className="w-4 h-4 text-green-600 shrink-0" />
                                            <p className="text-sm text-green-700 font-medium">Message sent successfully!</p>
                                        </div>
                                    )}

                                    {/* Actions */}
                                    <div className="flex items-center justify-between pt-1 border-t border-gray-100">
                                        <div className="flex items-center gap-2">
                                            <input
                                                ref={fileInputRef}
                                                type="file"
                                                multiple
                                                className="hidden"
                                                onChange={handleFileChange}
                                                accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt,.zip"
                                            />
                                            <button
                                                onClick={() => fileInputRef.current?.click()}
                                                disabled={uploading}
                                                className="flex items-center gap-1.5 rounded-xl border border-gray-200 bg-white px-4 py-2 text-sm font-semibold text-gray-600 hover:bg-gray-50 transition disabled:opacity-50"
                                            >
                                                {uploading
                                                    ? <Loader2 className="w-4 h-4 animate-spin" />
                                                    : <Paperclip className="w-4 h-4" />
                                                }
                                                {uploading ? "Attaching..." : "Attach File"}
                                            </button>
                                        </div>

                                        <button
                                            onClick={handleSend}
                                            disabled={sending || !subject.trim() || !message.trim()}
                                            className="flex items-center gap-2 rounded-xl px-6 py-2.5 text-sm font-bold text-white shadow-sm transition active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
                                            style={{ backgroundColor: "#1D74F5" }}
                                        >
                                            {sending
                                                ? <Loader2 className="w-4 h-4 animate-spin" />
                                                : <Send className="w-4 h-4" />
                                            }
                                            {sending ? "Sending..." : "Send Message"}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* ── Right sidebar ── */}
                        <div className="w-full lg:w-72 shrink-0 space-y-4">

                            {/* Admin contact card */}
                            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5">
                                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-4">You're messaging</p>
                                <div className="flex items-center gap-3 mb-3">
                                    <div className="w-12 h-12 rounded-2xl bg-blue-700 flex items-center justify-center text-white font-black text-base shrink-0">
                                        CA
                                    </div>
                                    <div>
                                        <p className="text-sm font-black text-gray-900">Conncct Admin</p>
                                        <p className="text-[11px] text-gray-400">Support & Operations</p>
                                    </div>
                                </div>
                                <div className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-full bg-green-50 border border-green-200 w-fit">
                                    <span className="w-1.5 h-1.5 rounded-full bg-green-500" />
                                    <span className="text-[10px] font-black text-green-700 tracking-wide">TYPICALLY REPLIES IN 24H</span>
                                </div>
                                <p className="text-[11px] text-gray-500 mt-3 leading-relaxed">
                                    Your message goes directly to the Conncct team. Include as much detail as possible.
                                </p>
                            </div>

                            {/* Sent history */}
                            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                                <div className="px-5 py-4 border-b border-gray-100">
                                    <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Sent Messages</p>
                                </div>

                                {loadingHistory ? (
                                    <div className="flex items-center justify-center py-8">
                                        <Loader2 className="w-5 h-5 animate-spin text-gray-300" />
                                    </div>
                                ) : sentMessages.length === 0 ? (
                                    <div className="flex flex-col items-center justify-center py-8 px-4 text-center">
                                        <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center mb-2">
                                            <MessageSquare className="w-5 h-5 text-gray-300" />
                                        </div>
                                        <p className="text-xs text-gray-400 font-medium">No messages sent yet</p>
                                    </div>
                                ) : (
                                    <div className="divide-y divide-gray-50 max-h-[420px] overflow-y-auto">
                                        {sentMessages.map(msg => (
                                            <button
                                                key={msg.id}
                                                onClick={() => setSelectedMsg(selectedMsg?.id === msg.id ? null : msg)}
                                                className="w-full text-left px-5 py-3.5 hover:bg-gray-50 transition"
                                            >
                                                <div className="flex items-start justify-between gap-2">
                                                    <p className="text-xs font-bold text-gray-900 truncate flex-1">{msg.subject}</p>
                                                    <span className="text-[10px] text-gray-400 shrink-0 flex items-center gap-1 mt-0.5">
                                                        <Clock className="w-2.5 h-2.5" />
                                                        {timeAgo(msg.sentAt)}
                                                    </span>
                                                </div>
                                                <p className="text-[11px] text-gray-500 truncate mt-0.5">
                                                    {stripHtml(msg.message).slice(0, 60)}
                                                </p>
                                                {msg.replyToTitle && (
                                                    <p className="text-[10px] text-blue-500 mt-1 truncate inline-flex items-center gap-1"><Reply className="w-2.5 h-2.5" /> {msg.replyToTitle}</p>
                                                )}
                                                {msg.attachments.length > 0 && (
                                                    <span className="inline-flex items-center gap-1 mt-1 text-[10px] text-gray-400">
                                                        <Paperclip className="w-2.5 h-2.5" />
                                                        {msg.attachments.length} attachment{msg.attachments.length > 1 ? "s" : ""}
                                                    </span>
                                                )}
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Expanded message detail (inline drawer) */}
                    {selectedMsg && (
                        <div className="mt-4 bg-white rounded-2xl border border-blue-200 shadow-sm overflow-hidden">
                            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
                                <div>
                                    <p className="text-sm font-black text-gray-900">{selectedMsg.subject}</p>
                                    <p className="text-[11px] text-gray-400 mt-0.5">
                                        Sent {new Date(selectedMsg.sentAt).toLocaleDateString("en-US", {
                                            weekday: "long", year: "numeric", month: "long", day: "numeric",
                                            hour: "2-digit", minute: "2-digit",
                                        })}
                                    </p>
                                </div>
                                <button onClick={() => setSelectedMsg(null)} className="text-gray-400 hover:text-gray-700 transition">
                                    <X className="w-5 h-5" />
                                </button>
                            </div>
                            <div className="px-6 py-5 space-y-4">
                                {selectedMsg.replyToTitle && (
                                    <div className="flex items-center gap-2 text-xs text-blue-600 font-semibold">
                                        <ChevronRight className="w-3.5 h-3.5" />
                                        In reply to: {selectedMsg.replyToTitle}
                                    </div>
                                )}
                                <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-line">{selectedMsg.message}</p>
                                {selectedMsg.attachments.length > 0 && (
                                    <div className="space-y-2 pt-2 border-t border-gray-100">
                                        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Attachments</p>
                                        {selectedMsg.attachments.map((att, i) => {
                                            const isImg = att.type?.startsWith("image/");
                                            const isData = att.url?.startsWith("data:");
                                            return isImg ? (
                                                <div key={i} className="space-y-1.5">
                                                    <img src={att.url} alt={att.name} className="rounded-xl border border-gray-200 max-h-60 object-cover" />
                                                    <a href={att.url} download={att.name}
                                                        className="text-xs font-semibold text-blue-600 hover:text-blue-800 transition">
                                                        Download {att.name}
                                                    </a>
                                                </div>
                                            ) : (
                                                <a key={i}
                                                    href={att.url}
                                                    {...(isData ? { download: att.name } : { target: "_blank", rel: "noopener noreferrer" })}
                                                    className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl border border-gray-200 hover:border-blue-300 transition group">
                                                    <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center shrink-0">
                                                        <Paperclip className="w-4 h-4 text-blue-600" />
                                                    </div>
                                                    <p className="text-xs font-semibold text-gray-900 truncate group-hover:text-blue-600 flex-1">{att.name}</p>
                                                </a>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </main>
        </div>
    );
}
